import { useState, useEffect, useMemo } from 'react';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import AssetPreviewModal from '../shared/AssetPreviewModal';
import { HiOutlineDownload, HiOutlineTrash, HiOutlineSearch, HiOutlineX, HiOutlineEye } from 'react-icons/hi';

const SALES_ASSET_LABELS = {
  sales_proposal: 'Proposal',
  sales_deck: 'Sales Deck',
};

export default function SalesAssetHistory() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewAsset, setPreviewAsset] = useState(null);
  const { addToast } = useToast();

  useEffect(() => {
    api.get('/sales/assets').then(({ data }) => {
      setAssets(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this failed generation?')) return;
    try {
      await api.delete(`/sales/assets/${id}`);
      setAssets(prev => prev.filter(a => a.id !== id));
      addToast('Failed generation removed');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to delete', 'error');
    }
  };

  // Parse client context from extraOptions for display and search
  const getClientContext = (asset) => {
    try {
      const opts = asset.extraOptions ? JSON.parse(asset.extraOptions) : {};
      return opts.clientContext || {};
    } catch { return {}; }
  };

  const filtered = useMemo(() => {
    let result = assets;

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(a => a.assetType === typeFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a => {
        const ctx = getClientContext(a);
        const searchFields = [
          ctx.companyName, ctx.clientName, ctx.clientEmail, ctx.industry,
          ctx.useCase, ctx.productsInterested, ctx.location,
          a.topic?.title, a.assetType
        ].filter(Boolean).join(' ').toLowerCase();
        return searchFields.includes(q);
      });
    }

    return result;
  }, [assets, typeFilter, searchQuery]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Asset History</h1>
      <p className="text-gray-500 mb-6">View and download all your generated sales assets.</p>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {/* Search bar */}
        <div className="relative flex-1">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by company name, contact, industry, use case..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <HiOutlineX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Type filter pills */}
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => setTypeFilter('all')} className={`px-3 py-1.5 rounded-full text-sm font-medium ${typeFilter === 'all' ? 'bg-v2-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
          {Object.entries(SALES_ASSET_LABELS).map(([type, label]) => (
            <button key={type} onClick={() => setTypeFilter(type)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${typeFilter === type ? 'bg-v2-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{label}</button>
          ))}
        </div>
      </div>

      {/* Results count */}
      {(searchQuery || typeFilter !== 'all') && (
        <div className="text-sm text-gray-500 mb-3">
          Showing {filtered.length} of {assets.length} assets
          {searchQuery && <span> matching "<strong>{searchQuery}</strong>"</span>}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
          {searchQuery ? `No assets found matching "${searchQuery}"` : 'No assets found. Generate your first asset to see it here!'}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Contact</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Topic</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(asset => {
                const ctx = getClientContext(asset);
                return (
                  <tr key={asset.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{SALES_ASSET_LABELS[asset.assetType] || asset.assetType}</td>
                    <td className="px-4 py-3">
                      {ctx.companyName ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{ctx.companyName}</div>
                          {ctx.industry && <div className="text-xs text-gray-400">{ctx.industry}</div>}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {ctx.clientName ? (
                        <div>
                          <div className="text-sm text-gray-900">{ctx.clientName}</div>
                          {ctx.clientTitle && <div className="text-xs text-gray-400">{ctx.clientTitle}</div>}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{asset.topic?.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(asset.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${
                        asset.status === 'completed' ? 'bg-green-100 text-green-800' :
                        asset.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{asset.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center">
                        {asset.status === 'completed' && asset.filePath && (
                          <>
                            <button onClick={() => setPreviewAsset(asset)} className="text-gray-500 hover:text-v2-blue" title="Preview">
                              <HiOutlineEye className="w-5 h-5" />
                            </button>
                            <a href={`${API_HOST}/api/sales/assets/${asset.id}/download`} className="text-v2-blue hover:text-blue-700" title="Download">
                              <HiOutlineDownload className="w-5 h-5" />
                            </a>
                          </>
                        )}
                        {asset.status === 'failed' && (
                          <button onClick={() => handleDelete(asset.id)} className="text-gray-400 hover:text-red-600" title="Remove failed generation">
                            <HiOutlineTrash className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal */}
      {previewAsset && (
        <AssetPreviewModal
          previewUrl={`${API_HOST}/api/sales/assets/${previewAsset.id}/preview`}
          downloadUrl={`${API_HOST}/api/sales/assets/${previewAsset.id}/download`}
          fileFormat={previewAsset.fileFormat}
          assetLabel={SALES_ASSET_LABELS[previewAsset.assetType] || previewAsset.assetType}
          onClose={() => setPreviewAsset(null)}
        />
      )}
    </div>
  );
}
